import React, { useCallback, useEffect, useMemo } from 'react';
import { Alert, Box, CircularProgress, Typography } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { useRecoilValue } from 'recoil';
import DynamicBreadcrumbs from '../../../components/Shared/Breadcrumb/index.tsx';
import { useSnackBar } from '../../../components/FlowBuilder/Common/SnackBar/index.tsx';
import { Button } from '../../../components/ui';
import { ROUTES } from '../../../constants/routes';
import { buildBreadcrumbs, createBreadcrumbNavigateHandler } from '../../../config/navigation/breadcrumbs';
import { userInformationAtom } from '../../../Services/_states/auth.js';
import { FeatureThemeProvider } from '../../../theme/FeatureThemeProvider.jsx';
import ProfilePermissionsEditor from '../components/permissions/ProfilePermissionsEditor.jsx';
import BusinessFunctionsPermissionSection from '../components/permissions/BusinessFunctionsPermissionSection.jsx';
import useProfileManagementPermissions from '../hooks/useProfileManagementPermissions.js';
import useProfilePermissions from '../hooks/useProfilePermissions.js';
import useProfileSx from '../hooks/useProfileSx.js';
import { buildPermissionTreeSections } from '../utils/permissionTreeUtils';
import { isOwnAssignedProfile } from '../utils/profileAccessUtils.js';

export default function ProfilePermissionsPage() {
  return (
    <FeatureThemeProvider>
      <ProfilePermissionsPageContent />
    </FeatureThemeProvider>
  );
}

function ProfilePermissionsPageContent() {
  const { profileId } = useParams();
  const navigate = useNavigate();
  const loggedInUser = useRecoilValue(userInformationAtom);
  const styles = useProfileSx();
  const { showSnackBar } = useSnackBar();
  const isOwnProfile = isOwnAssignedProfile(loggedInUser, profileId);
  const { canManagePermissions } = useProfileManagementPermissions();

  useEffect(() => {
    if (isOwnProfile || !canManagePermissions) {
      navigate(ROUTES.MANAGEMENT.PROFILE_MANAGEMENT, { replace: true });
    }
  }, [canManagePermissions, isOwnProfile, navigate]);

  const {
    catalog,
    profile,
    grants,
    resourceTree,
    isLoading,
    isResourceTreeLoading,
    isSaving,
    isDirty,
    error,
    saveGrants,
    updateFeatureGrant,
    updateObjectGrant,
    updateNodeGrants,
    updateResourceGrants,
    patchGrants,
  } = useProfilePermissions(profileId);

  const businessFunctionsSection = useMemo(
    () => buildPermissionTreeSections(catalog).find(
      (section) => section.codename === 'business_functions_management'
    ),
    [catalog]
  );

  const breadcrumbsData = useMemo(
    () => buildBreadcrumbs([
      { label: 'Management', href: ROUTES.MANAGEMENT.PROFILE_MANAGEMENT },
      { label: 'Profile Management', href: ROUTES.MANAGEMENT.PROFILE_MANAGEMENT },
      { label: 'Permissions' },
    ]),
    []
  );

  const handleSave = useCallback(async () => {
    const result = await saveGrants();
    if (result.ok) {
      showSnackBar('Permissions saved successfully.', 'success');
      return;
    }
    showSnackBar(result.message || 'Failed to save permissions.', 'error');
  }, [saveGrants, showSnackBar]);

  if (isOwnProfile || !canManagePermissions) {
    return null;
  }

  return (
    <Box sx={styles.permissionsPageRoot}>
      <DynamicBreadcrumbs
        items={breadcrumbsData}
        onBreadcrumbClick={createBreadcrumbNavigateHandler(navigate)}
      />

      <Box sx={styles.permissionsPageHeader}>
        <Typography variant="h5" sx={styles.permissionsTitle}>
          {profile?.name ? `${profile.name} — Permissions` : 'Profile Permissions'}
        </Typography>
        <Typography sx={styles.permissionsSubtitle}>
          Each module title has a View toggle on the same row. Expand to fine-tune
          individual features below.
        </Typography>
      </Box>

      {isLoading ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 3 }}>
          <CircularProgress size={22} />
          <Typography>Loading permissions...</Typography>
        </Box>
      ) : null}

      {!isLoading && error ? <Alert severity="error">{error}</Alert> : null}

      {!isLoading && !error ? (
        <>
          <ProfilePermissionsEditor
            catalog={catalog}
            grants={grants}
            disabled={isSaving}
            onNodeGrantChange={updateNodeGrants}
            onFeatureChange={updateFeatureGrant}
            onObjectChange={updateObjectGrant}
          />
          {businessFunctionsSection ? (
            <BusinessFunctionsPermissionSection
              section={businessFunctionsSection}
              grants={grants}
              resourceTree={resourceTree}
              isResourceTreeLoading={isResourceTreeLoading}
              disabled={isSaving}
              onGrantsPatch={patchGrants}
            />
          ) : null}
        </>
      ) : null}

      <Box sx={styles.permissionsActions}>
        <Button
          variant="secondary"
          onClick={() => navigate('/r/management/profile-management')}
          disabled={isSaving}
        >
          Back to profiles
        </Button>
        <Button
          variant="primary"
          loading={isSaving}
          disabled={isLoading || Boolean(error) || !isDirty}
          onClick={handleSave}
        >
          Save Permissions
        </Button>
      </Box>
    </Box>
  );
}
